// PropertyDetailView.jsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Avatar,
  CircularProgress,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Stack,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import BatteryAlertIcon from "@mui/icons-material/BatteryAlert";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from "@mui/icons-material/Business";
import GenerateVendorLinkDialog from "../maintenance-vendor/GenerateVendorLinkDialog";

import { useAuth } from "../../hooks/useAuth";
import useUserDevices from "../../hooks/useUserDevices";
import { usePropertyLock, useAvailableLocksForProperty } from "../../hooks/usePropertyLocks";
import AccessCodeManager from "./AccessCodeManager";
import AccessLogs from "./AccessLogs";
import TenantsList from "./TenantsList";
import { getTenants, getAuthorisedUsers } from "../../services/tenantService";

import {
  loadLocks,
  pollLocks,
  connectWebview,
  handleLockAction,
  handleUpdateLockName,
  openAccessCodeDialog,
  closeAccessCodeDialog,
  openLogsDialog,
  closeLogsDialog,
  formatLockName,
  parseLockName,
  handleTransferLocks,
} from "./smartLocksHelpers";

import styles from "./DeviceList.styles";
// import { Add } from "@mui/icons-material";
import AddTenantDialog from "../add-tenants/addTenants";
import AuthorisedUsersList from "./AuthorisedusersList";
import useSmartLockSubscriptionGuard from "../../hooks/useSmartLockSubscriptionGuard";
import FindMeATenantButton from "./FindMeATenantButton";

const POLLING_INTERVAL_MS = 5000;

const PropertyDetailView = ({ property, onBack }) => {
  const theme = useTheme();
  const s = styles(theme);

  const { currentUser } = useAuth();
  const { requireSubscription, subscriptionGuardLoading, modal } = useSmartLockSubscriptionGuard();
  const { devices, loading: devicesLoading, addDevice, updateDevice, deleteDevice } = useUserDevices();

  // Property-based lock management (NEW - fetches from property_locks collection)
  // Note: property uses `propertyId` as the identifier field
  const listingId = property?.propertyId || property?.id;

  const {
    locks: propertyLocks,
    loading: propertyLocksLoading,
    associateLock,
    refetch: refetchPropertyLocks,
  } = usePropertyLock(listingId);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [authUsersCount, setAuthUsersCount] = useState(0);

  // Tenant refresh trigger
  const [tenantRefreshTrigger, setTenantRefreshTrigger] = useState(0);
  const [tenantCount, setTenantCount] = useState(0);

  // ---------- Smart Locks State ----------
  const [legacyLocks, setLegacyLocks] = useState([]); // Legacy locks loaded by device name matching
  const [legacyLocksLoading, setLegacyLocksLoading] = useState(true);
  const [iframeUrl, setIframeUrl] = useState("");
  const [showIframe, setShowIframe] = useState(false);

  // Use refs to track Seam Connect state (avoids re-render timing issues)
  const waitingForSeamConnectRef = useRef(false);
  const locksBeforeConnectRef = useRef([]);

  const [openAccessCodeDialogState, setOpenAccessCodeDialog] = useState(false);
  const [selectedLockId, setSelectedLockId] = useState(null);

  const [lockActionLoading, setLockActionLoading] = useState({});
  const [openLogsForLockId, setOpenLogsForLockId] = useState(null);

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedLock, setSelectedLock] = useState(null);

  // Rename
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameLockDetails, setRenameLockDetails] = useState({
    address: "",
    city: "",
    state: "",
    zip: "",
    lockName: "",
  });
  const [renameLockId, setRenameLockId] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);

  // Selection + transfer
  const [selectedLockIds, setSelectedLockIds] = useState(new Set());
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferEmail, setTransferEmail] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);

  // Associate lock dialog (NEW)
  const [associateDialogOpen, setAssociateDialogOpen] = useState(false);
  const [selectedLockToAssociate, setSelectedLockToAssociate] = useState("");
  const [associating, setAssociating] = useState(false);

  // Vendor link generation dialog
  const [vendorLinkDialogOpen, setVendorLinkDialogOpen] = useState(false);

  // Add tenants dialog (placeholder for now, can be implemented later)
  const [addTenantDialogOpen, setAddTenantDialogOpen] = useState(false);
  // Get available locks for association
  const {
    availableLocks,
    loading: availableLocksLoading,
    refetch: refetchAvailableLocks,
  } = useAvailableLocksForProperty(listingId);

  // ---------- Devices State ----------
  const [newDevice, setNewDevice] = useState({
    name: "",
    type: "",
    propertyAddress: "",
    notes: "",
    model: "",
    serial: "",
    portalLink: "",
  });
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [showDeviceForm, setShowDeviceForm] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Build property address string for matching
  const propertyAddress = useMemo(() => {
    if (!property) return "";
    const parts = [
      property.address,
      property.city,
      property.state,
      property.zip,
    ].filter(Boolean);
    return parts.join(", ");
  }, [property]);

  // Combined loading state
  const locksLoading = propertyLocksLoading || legacyLocksLoading;

  // Use property-based locks as primary source
  // This fetches locks from the property_locks collection by listingId
  const matchingLocks = useMemo(() => {
    // Property locks from the new property_locks collection (primary source)
    const propertyLocksList = propertyLocks || [];

    // For legacy support: also check if any legacy locks match by address
    // This handles locks that were associated before the property-based system
    const legacyMatchingLocks = legacyLocks.filter((lock) => {
      // Skip if this lock is already in propertyLocks
      if (propertyLocksList.some(pl => pl.lockId === lock.lockId)) {
        return false;
      }

      const lockAddressFull = lock.deviceName?.split(" - ")[0] || "";
      if (!lockAddressFull) return false;

      const propertyStreet = (property?.address || "").toLowerCase().trim();
      const propertyCity = (property?.city || "").toLowerCase().trim();
      const propertyState = (property?.state || "").toLowerCase().trim();

      const lockAddressLower = lockAddressFull.toLowerCase().trim();
      const lockParts = lockAddressLower.split(", ").map(p => p.trim());

      if (lockParts.length >= 2) {
        const lockStreet = lockParts[0];
        const lockCity = lockParts[1];
        const lockStateZip = lockParts[2] || "";
        const lockState = lockStateZip.split(/\s+/)[0]?.toLowerCase() || "";

        const streetMatch = propertyStreet && lockStreet && lockStreet.includes(propertyStreet);
        const cityMatch = propertyCity && lockCity && lockCity.includes(propertyCity);
        const stateMatch = propertyState && lockState && lockState.includes(propertyState);

        return streetMatch && cityMatch && stateMatch;
      }

      return false;
    });

    // Combine: property locks first, then any legacy matches
    return [...propertyLocksList, ...legacyMatchingLocks];
  }, [propertyLocks, legacyLocks, property]);

  // Filter devices by property address
  const matchingDevices = useMemo(() => {
    if (!propertyAddress) return devices;
    return devices.filter((device) => {
      const deviceAddress = device.propertyAddress || "";
      return deviceAddress.toLowerCase().includes(propertyAddress.toLowerCase());
    });
  }, [devices, propertyAddress]);

  // ---------- Effects ----------
  // Load legacy locks (for backward compatibility with name-based matching)
  useEffect(() => {
    if (currentUser) {
      setLegacyLocksLoading(true);
      loadLocks(currentUser, (loadedLocks) => {
        setLegacyLocks(loadedLocks || []);
        setLegacyLocksLoading(false);
      });
    }
  }, [currentUser]);

  // ── Eager count fetches (so tab labels show counts before tab is opened) ──────
  useEffect(() => {
    if (!listingId) return;
    getTenants(listingId)
      .then((data) => setTenantCount(data.length))
      .catch((err) => console.error("[PropertyDetailView] tenant count fetch:", err));
  }, [listingId]);

  useEffect(() => {
    if (!listingId) return;
    getAuthorisedUsers(listingId)
      .then((data) => setAuthUsersCount(data.length))
      .catch((err) => console.error("[PropertyDetailView] auth users count fetch:", err));
  }, [listingId]);

  // Poll legacy locks for status updates
  useEffect(() => {
    if (!currentUser || legacyLocks.length === 0) return;
    const id = setInterval(() => {
      pollLocks(currentUser, legacyLocks, setLegacyLocks);
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(id);
  }, [legacyLocks, currentUser]);

  // Update selection state when locks change
  useEffect(() => {
    if (!matchingLocks.length) {
      setSelectedLockIds(new Set());
      setSelectAllChecked(false);
    } else {
      setSelectAllChecked(
        selectedLockIds.size > 0 && selectedLockIds.size === matchingLocks.length
      );
    }
  }, [matchingLocks, selectedLockIds.size]);

  // Pre-fill property address in device form
  useEffect(() => {
    if (property && showDeviceForm) {
      setNewDevice((prev) => ({
        ...prev,
        propertyAddress: propertyAddress,
      }));
    }
  }, [property, propertyAddress, showDeviceForm]);

  // ---------- Menu ----------
  const handleMenuClick = (event, lock) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedLock(lock);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedLock(null);
  };

  // ---------- Rename ----------
  const handleOpenRenameDialog = (lock) => {
    setRenameDialogOpen(true);
    setRenameLockId(lock.lockId);

    // Parse the deviceName to extract address components and lock name
    const parsed = parseLockName(lock.deviceName || "");
    setRenameLockDetails({
      address: parsed.address || property.address || "",
      city: parsed.city || property.city || "",
      state: parsed.state || property.state || "",
      zip: parsed.zip || property.zip || "",
      lockName: parsed.lockName,
    });
  };
  const handleCloseRenameDialog = () => {
    setRenameDialogOpen(false);
    setRenameLockId(null);
    setRenameLockDetails({
      address: "",
      city: "",
      state: "",
      zip: "",
      lockName: "",
    });
  };
  const handleConfirmRename = async () => {
    if (!renameLockId) return;
    try {
      if (
        !renameLockDetails.address ||
        !renameLockDetails.city ||
        !renameLockDetails.state ||
        !renameLockDetails.zip ||
        !renameLockDetails.lockName
      ) {
        setSnackbar({
          open: true,
          message:
            "Please fill in all fields (address, city, state, zip, and lock name)",
          severity: "error",
        });
        return;
      }

      const formattedLockName = formatLockName(renameLockDetails);
      setIsRenaming(true);

      await handleUpdateLockName(
        renameLockId,
        formattedLockName,
        currentUser,
        setLegacyLocks,
        setIsRenaming
      );

      // Wait for backend to process, then reload both property locks and legacy locks
      await new Promise((resolve) => setTimeout(resolve, 500));
      refetchPropertyLocks();
      setLegacyLocksLoading(true);
      loadLocks(currentUser, (loadedLocks) => {
        setLegacyLocks(loadedLocks || []);
        setLegacyLocksLoading(false);
      });

      handleCloseRenameDialog();
      setSnackbar({
        open: true,
        message: "Lock renamed successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error renaming lock:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to rename lock. Please try again.",
        severity: "error",
      });
    } finally {
      setIsRenaming(false);
    }
  };

  // ---------- Selection & Transfer ----------
  const toggleSelectLock = (lockId) => {
    setSelectedLockIds((prev) => {
      const next = new Set(prev);
      next.has(lockId) ? next.delete(lockId) : next.add(lockId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectAllChecked((prev) => {
      const nextVal = !prev;
      if (nextVal)
        setSelectedLockIds(new Set(matchingLocks.map((l) => l.lockId)));
      else setSelectedLockIds(new Set());
      return nextVal;
    });
  };

  const openTransferDialogFor = (lockIds) => {
    setSelectedLockIds(new Set(lockIds));
    setTransferDialogOpen(true);
  };

  const handleConfirmTransfer = async () => {
    if (!transferEmail || selectedLockIds.size === 0) return;
    try {
      setTransferLoading(true);
      await handleTransferLocks(
        Array.from(selectedLockIds),
        transferEmail,
        currentUser,
        setTransferLoading
      );
      setTransferLoading(false);
      setTransferDialogOpen(false);
      setTransferEmail("");
      setSelectedLockIds(new Set());
      setSelectAllChecked(false);
      setSnackbar({
        open: true,
        message: "Locks transferred successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Transfer failed", err);
      setTransferLoading(false);
      setSnackbar({
        open: true,
        message: err.message || "Failed to transfer locks. Please try again.",
        severity: "error",
      });
    }
  };

  // ---------- Handle Seam Connect Dialog Close ----------
  const handleSeamConnectClose = async () => {
    console.log("[PropertyDetailView] Seam Connect dialog closing...");
    console.log("[PropertyDetailView] waitingForSeamConnectRef:", waitingForSeamConnectRef.current);
    console.log("[PropertyDetailView] locksBeforeConnectRef:", locksBeforeConnectRef.current.length, "locks");

    setShowIframe(false);

    if (waitingForSeamConnectRef.current && currentUser && listingId) {
      const savedLocksBefore = [...locksBeforeConnectRef.current];
      waitingForSeamConnectRef.current = false;
      locksBeforeConnectRef.current = [];

      console.log("[PropertyDetailView] Will auto-associate after detecting new lock...");

      // Show loading state
      setLegacyLocksLoading(true);
      setSnackbar({
        open: true,
        message: "Detecting new lock... Please wait.",
        severity: "info",
      });

      // Helper function to fetch and detect new locks with retries
      const detectAndAssociateNewLock = async (attempt = 1, maxAttempts = 5) => {
        console.log(`[PropertyDetailView] Attempt ${attempt}/${maxAttempts} - Fetching locks...`);

        return new Promise((resolve) => {
          loadLocks(currentUser, async (loadedLocks) => {
            const newLocks = loadedLocks || [];
            console.log("[PropertyDetailView] Fetched", newLocks.length, "locks");
            setLegacyLocks(newLocks);

            // Find new locks
            const previousLockIds = new Set(savedLocksBefore.map(l => l.lockId));
            const newlyAddedLocks = newLocks.filter(lock => !previousLockIds.has(lock.lockId));

            console.log("[PropertyDetailView] New locks found:", newlyAddedLocks.length);

            if (newlyAddedLocks.length > 0) {
              // Found new locks - associate them
              setLegacyLocksLoading(false);

              for (const lock of newlyAddedLocks) {
                console.log("[PropertyDetailView] Associating lock:", lock.lockId, "with property:", listingId);
                try {
                  await associateLock(lock.lockId);
                  console.log("[PropertyDetailView] SUCCESS - Lock associated!");
                  setSnackbar({
                    open: true,
                    message: `Lock "${lock.deviceName || 'Smart Lock'}" added to this property!`,
                    severity: "success",
                  });
                } catch (error) {
                  console.error("[PropertyDetailView] FAILED to associate:", error);
                  setSnackbar({
                    open: true,
                    message: `Failed to add lock: ${error.message || error}`,
                    severity: "error",
                  });
                }
              }
              refetchPropertyLocks();
              resolve(true);
            } else if (attempt < maxAttempts) {
              // No new locks found yet - retry after a delay
              console.log(`[PropertyDetailView] No new locks detected, retrying in 2 seconds...`);
              setTimeout(() => {
                detectAndAssociateNewLock(attempt + 1, maxAttempts).then(resolve);
              }, 2000);
            } else {
              // Max attempts reached
              setLegacyLocksLoading(false);
              console.log("[PropertyDetailView] Max attempts reached, no new locks found");
              setSnackbar({
                open: true,
                message: "Lock added but auto-association failed. Use 'Use Existing Lock' to add it manually.",
                severity: "warning",
              });
              resolve(false);
            }
          });
        });
      };

      // Start detection after initial delay for backend to process
      setTimeout(() => {
        detectAndAssociateNewLock();
      }, 3000);
    }
  };

  // ---------- Associate Lock with Property ----------
  const handleOpenAssociateDialog = () => {
    setAssociateDialogOpen(true);
    setSelectedLockToAssociate("");
    refetchAvailableLocks();
  };

  const handleFindMeATenantAgreed = () => {
    // TODO next step: open property info / payment flow
    console.log("Agreement confirmed — open next step");
  };

  const handleCloseAssociateDialog = () => {
    setAssociateDialogOpen(false);
    setSelectedLockToAssociate("");
  };

  const handleAssociateLock = async () => {
    if (!selectedLockToAssociate) {
      setSnackbar({
        open: true,
        message: "Please select a lock to associate",
        severity: "error",
      });
      return;
    }

    setAssociating(true);
    try {
      await associateLock(selectedLockToAssociate);
      setSnackbar({
        open: true,
        message: "Lock successfully associated with this property!",
        severity: "success",
      });
      handleCloseAssociateDialog();
      refetchAvailableLocks();
    } catch (error) {
      console.error("Error associating lock:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || "Failed to associate lock",
        severity: "error",
      });
    } finally {
      setAssociating(false);
    }
  };

  // ---------- Devices ----------
  const handleInputChange = (field, value) => {
    setNewDevice((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDevice = async () => {
    try {
      if (!newDevice.name || !newDevice.type) {
        setSnackbar({
          open: true,
          message: "Please fill in device name and type",
          severity: "error",
        });
        return;
      }

      if (editingDeviceId) {
        await updateDevice(editingDeviceId, { ...newDevice });
        setEditingDeviceId(null);
        setSnackbar({
          open: true,
          message: "Device updated successfully!",
          severity: "success",
        });
      } else {
        await addDevice({ ...newDevice });
        setSnackbar({
          open: true,
          message: "Device added successfully!",
          severity: "success",
        });
      }

      // Clear form
      setNewDevice({
        name: "",
        type: "",
        propertyAddress: propertyAddress,
        notes: "",
        model: "",
        serial: "",
        portalLink: "",
      });
      setShowDeviceForm(false);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to save device",
        severity: "error",
      });
    }
  };

  const handleEditDevice = (device) => {
    setEditingDeviceId(device.id);
    setNewDevice({
      name: device.name || "",
      type: device.type || "",
      propertyAddress: device.propertyAddress || "",
      notes: device.notes || "",
      model: device.model || "",
      serial: device.serial || "",
      portalLink: device.portalLink || "",
    });
    setShowDeviceForm(true);
  };

  const handleDeleteDevice = async (deviceId) => {
    if (window.confirm("Are you sure you want to delete this device?")) {
      try {
        await deleteDevice(deviceId);
        setSnackbar({
          open: true,
          message: "Device deleted successfully!",
          severity: "success",
        });
      } catch (err) {
        console.error(err);
        setSnackbar({
          open: true,
          message: "Failed to delete device",
          severity: "error",
        });
      }
    }
  };

  const currentLockName =
    matchingLocks.find((lk) => lk.lockId === selectedLockId)?.deviceName ?? "";

  // ---------- Render ----------
  return (
    <Box sx={s.container}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <IconButton onClick={onBack} aria-label="Back">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {property?.address || "Property Details"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {propertyAddress}
          </Typography>
        </Box>
        {/* Maintenance Vendor Link & Add Tenant Buttons */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <FindMeATenantButton                      
            uid={currentUser?.uid}
            email={currentUser?.email}
            onAgreed={handleFindMeATenantAgreed}
            variant="contained"
            size="small"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<BusinessIcon />}
            onClick={() => setVendorLinkDialogOpen(true)}
            sx={{
              ml: { xs: 0, sm: 2 },
              mt: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Generate Vendor Link
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setAddTenantDialogOpen(true)}
            sx={{
              ml: { xs: 0, sm: 2 },
              mt: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Add Tenant
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`Smart Locks (${matchingLocks.length})`} />
          <Tab label={`Devices (${matchingDevices.length})`} />
          <Tab label={`Tenants (${tenantCount})`} />
          <Tab label={`Authorized Users (${authUsersCount})`} />
        </Tabs>
      </Box>

      {/* Smart Locks Tab */}
      {activeTab === 0 && (
        <Box>
          {/* Header */}
          <Box sx={s.headerWrap}>
            <Box sx={s.headerLeft}>
              <Typography variant="h6" sx={s.title}>
                Smart Locks{" "}
                <Box component="span" sx={s.deviceCount}>
                  {matchingLocks.length}
                </Box>
              </Typography>

              <Box sx={s.bulkRow}>
                <Checkbox
                  checked={selectAllChecked}
                  onChange={toggleSelectAll}
                  size="small"
                  inputProps={{ "aria-label": "Select all locks" }}
                />
                <Typography variant="body2" noWrap>
                  Select all
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SwapHorizIcon />}
                  onClick={() =>
                    openTransferDialogFor(Array.from(selectedLockIds))
                  }
                  disabled={selectedLockIds.size === 0}
                  sx={s.transferBtn}
                >
                  Transfer selected ({selectedLockIds.size || 0})
                </Button>
              </Box>
            </Box>

            <Stack direction="row" spacing={1}>
              {/* Connect a new lock via Seam AND auto-associate with this property */}
              <Button
                variant="contained"
                sx={s.addBtn}
                onClick={() => {
                  requireSubscription(() => {
                    // Save current locks so we can detect new ones after Seam Connect
                    locksBeforeConnectRef.current = [...legacyLocks];
                    waitingForSeamConnectRef.current = true;
                    console.log("[PropertyDetailView] Opening Seam Connect, saved", legacyLocks.length, "existing locks");

                    // Store property info in sessionStorage for SeamSuccess to use
                    sessionStorage.setItem('pendingLockAssociation', JSON.stringify({
                      listingId: listingId,
                      propertyAddress: property?.address || 'Property',
                      returnPath: window.location.pathname,
                    }));

                    connectWebview(currentUser, setIframeUrl, setShowIframe);
                  });
                }}
                disabled={subscriptionGuardLoading}
              >
                + Add Smart Lock
              </Button>
              {/* Associate an existing lock with this property */}
              {legacyLocks.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => requireSubscription(handleOpenAssociateDialog)}
                  disabled={subscriptionGuardLoading}
                >
                  Use Existing Lock
                </Button>
              )}
            </Stack>
          </Box>

          {/* Locks List */}
          {locksLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={s.list}>
              {matchingLocks.map((lock) => {
                const isSelected = selectedLockIds.has(lock.lockId);
                return (
                  <ListItem
                    key={lock.lockId}
                    sx={[
                      s.row,
                      isSelected && s.rowHighlighted,
                    ]}
                  >
                    <Box sx={s.selectBox}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleSelectLock(lock.lockId)}
                        size="small"
                        inputProps={{ "aria-label": `Select ${lock.deviceName}` }}
                      />
                    </Box>

                    <Box sx={s.infoWrap}>
                      <Avatar sx={s.avatar}>
                        <LockIcon />
                      </Avatar>
                      <Box sx={s.nameBlock}>
                        <Typography variant="body1" sx={s.deviceName}>
                          {lock.deviceName || "Unnamed Lock"}
                        </Typography>
                        <Typography variant="caption" sx={s.deviceType}>
                          {lock.manufacturer || "Smart Lock"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={s.metaWrap}>
                      <Tooltip title={lock.isLocked ? "Locked" : "Unlocked"}>
                        <Box sx={s.metaPill}>
                          {lock.isLocked ? (
                            <LockIcon className="locked" sx={s.lockIcon} />
                          ) : (
                            <LockOpenIcon className="unlocked" sx={s.lockIcon} />
                          )}
                          <Typography variant="caption">
                            {lock.isLocked ? "Locked" : "Unlocked"}
                          </Typography>
                        </Box>
                      </Tooltip>

                      {lock.batteryLevel !== undefined && (
                        <Tooltip
                          title={
                            lock.batteryLevel > 0.2
                              ? "Battery OK"
                              : "Low Battery"
                          }
                        >
                          <Box sx={s.metaPill}>
                            {lock.batteryLevel > 0.2 ? (
                              <BatteryFullIcon sx={s.batteryOk} />
                            ) : (
                              <BatteryAlertIcon sx={s.batteryLow} />
                            )}
                            <Typography variant="caption">
                              {(Number(lock.batteryLevel) * 100).toFixed(2)}%
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}

                      {lock.isOnline !== undefined && (
                        <Tooltip title={lock.isOnline ? "Online" : "Offline"}>
                          <Box sx={s.metaPill}>
                            <FiberManualRecordIcon
                              sx={[
                                s.dotOnline,
                                !lock.isOnline && { color: "#333333" },
                              ]}
                            />
                            <Typography variant="caption">
                              {lock.isOnline ? "Online" : "Offline"}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>

                    <Box sx={s.actionsRight}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          requireSubscription(() =>
                            handleLockAction(
                              lock.lockId,
                              lock.isLocked ? "unlock" : "lock",
                              currentUser,
                              setLockActionLoading,
                              matchingLocks,
                              (updatedLocks) => {
                                refetchPropertyLocks();
                                setLegacyLocks(prev => prev.map(l =>
                                  updatedLocks.find(ul => ul.lockId === l.lockId) || l
                                ));
                              }
                            )
                          )
                        }
                        disabled={lockActionLoading[lock.lockId]}
                      >
                        {lockActionLoading[lock.lockId] ? (
                          <CircularProgress size={20} />
                        ) : lock.isLocked ? (
                          <LockOpenIcon />
                        ) : (
                          <LockIcon />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, lock)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                );
              })}
              {matchingLocks.length === 0 && !locksLoading && (
                <ListItem>
                  <Typography variant="body2" color="text.secondary">
                    No smart locks found for this property. Click "Add Smart Lock" to connect one.
                  </Typography>
                </ListItem>
              )}
            </List>
          )}
        </Box>
      )}

      {/* Devices Tab */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">
              Devices{" "}
              <Box component="span" sx={s.deviceCount}>
                {matchingDevices.length}
              </Box>
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setShowDeviceForm(true);
                setEditingDeviceId(null);
                setNewDevice({
                  name: "",
                  type: "",
                  propertyAddress: propertyAddress,
                  notes: "",
                  model: "",
                  serial: "",
                  portalLink: "",
                });
              }}
            >
              Add Device
            </Button>
          </Box>

          {/* Device Form */}
          {showDeviceForm && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {editingDeviceId ? "Edit Device" : "Add New Device"}
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Device Name"
                  value={newDevice.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  fullWidth
                  required
                />
                <TextField
                  label="Device Type (e.g. Digital Lock, Modem)"
                  value={newDevice.type}
                  onChange={(e) => handleInputChange("type", e.target.value)}
                  fullWidth
                  required
                />
                <TextField
                  label="Property Address"
                  value={newDevice.propertyAddress}
                  onChange={(e) =>
                    handleInputChange("propertyAddress", e.target.value)
                  }
                  fullWidth
                />
                <TextField
                  label="Notes (Lock Codes, etc.)"
                  value={newDevice.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                />
                <TextField
                  label="Model"
                  value={newDevice.model}
                  onChange={(e) => handleInputChange("model", e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Serial Number"
                  value={newDevice.serial}
                  onChange={(e) => handleInputChange("serial", e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Portal Link"
                  value={newDevice.portalLink}
                  onChange={(e) =>
                    handleInputChange("portalLink", e.target.value)
                  }
                  fullWidth
                />
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    onClick={handleSaveDevice}
                    disabled={devicesLoading}
                  >
                    {editingDeviceId ? "Update" : "Add"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowDeviceForm(false);
                      setEditingDeviceId(null);
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Devices List */}
          {devicesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Serial</TableCell>
                  <TableCell>Portal Link</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matchingDevices.map((device) => (
                  <TableRow key={device.id}>
                    <TableCell>{device.name}</TableCell>
                    <TableCell>{device.type}</TableCell>
                    <TableCell>{device.propertyAddress}</TableCell>
                    <TableCell>{device.notes}</TableCell>
                    <TableCell>{device.model}</TableCell>
                    <TableCell>{device.serial}</TableCell>
                    <TableCell>
                      {device.portalLink ? (
                        <a
                          href={device.portalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {device.portalLink}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditDevice(device)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteDevice(device.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {matchingDevices.length === 0 && !devicesLoading && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      No devices found for this property. Add one above.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {/* Tenants Tab */}
      {activeTab === 2 && (
        <TenantsList
          propertyId={listingId}
          refreshTrigger={tenantRefreshTrigger}
          onTenantCountChange={setTenantCount}
          onAddTenant={() => setAddTenantDialogOpen(true)}
        />
      )}

      {activeTab === 3 && (
        <AuthorisedUsersList
          propertyId={listingId}
          onCountChange={setAuthUsersCount}
        />
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (!selectedLock) return;
            requireSubscription(() => {
              handleLockAction(
                selectedLock.lockId,
                selectedLock.isLocked ? "unlock" : "lock",
                currentUser,
                setLockActionLoading,
                matchingLocks,
                (updatedLocks) => {
                  refetchPropertyLocks();
                  setLegacyLocks(prev => prev.map(l =>
                    updatedLocks.find(ul => ul.lockId === l.lockId) || l
                  ));
                }
              );
              handleMenuClose();
            });
          }}
        >
          {selectedLock?.isLocked ? <LockOpenIcon /> : <LockIcon />}
          &nbsp;{selectedLock?.isLocked ? "Unlock" : "Lock"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!selectedLock) return;
            requireSubscription(() => {
              openAccessCodeDialog(
                selectedLock.lockId,
                setSelectedLockId,
                setOpenAccessCodeDialog
              );
              handleMenuClose();
            });
          }}
        >
          Manage Access Codes
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!selectedLock) return;
            requireSubscription(() => {
              openLogsDialog(selectedLock.lockId, setOpenLogsForLockId);
              handleMenuClose();
            });
          }}
        >
          View Logs
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!selectedLock) return;
            requireSubscription(() => {
              handleMenuClose();
              handleOpenRenameDialog(selectedLock);
            });
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Rename Lock
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!selectedLock) return;
            requireSubscription(() => {
              handleMenuClose();
              openTransferDialogFor([selectedLock.lockId]);
            });
          }}
        >
          <SwapHorizIcon sx={{ mr: 1 }} />
          Transfer Lock
        </MenuItem>
      </Menu>

      {/* Seam Connect Webview */}
      <Dialog
        open={showIframe}
        onClose={handleSeamConnectClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Connect Smart Lock</DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%", height: "600px" }}>
            <iframe
              src={iframeUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="Seam Connect"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSeamConnectClose} variant="contained" color="primary">
            Done - Close & Add Lock
          </Button>
        </DialogActions>
      </Dialog>
      {modal}

      {/* Associate Lock Dialog */}
      <Dialog
        open={associateDialogOpen}
        onClose={handleCloseAssociateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Smart Lock to Property</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {availableLocksLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            ) : availableLocks.length === 0 ? (
              <Alert severity="info">
                No available locks found. Click "Connect New Lock" to connect a smart lock to your account first.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select Lock</InputLabel>
                <Select
                  value={selectedLockToAssociate}
                  onChange={(e) => setSelectedLockToAssociate(e.target.value)}
                  label="Select Lock"
                >
                  {availableLocks.map((lock) => (
                    <MenuItem key={lock.lockId} value={lock.lockId}>
                      {lock.deviceName || "Unnamed Device"} ({lock.deviceType})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssociateDialog} disabled={associating}>
            Cancel
          </Button>
          <Button
            onClick={handleAssociateLock}
            variant="contained"
            disabled={associating || !selectedLockToAssociate}
          >
            {associating ? <CircularProgress size={24} /> : "Add to Property"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Access Code Manager */}
      <AccessCodeManager
        deviceType={matchingLocks.find((lk) => lk.lockId === selectedLockId)?.deviceType ?? ""}
        isOpen={openAccessCodeDialogState}
        onClose={() => closeAccessCodeDialog(setOpenAccessCodeDialog)}
        uid={currentUser?.uid}
        lockId={selectedLockId}
        lockName={currentLockName}
      />

      {/* Access Logs */}
      {openLogsForLockId && (
        <Dialog
          open={Boolean(openLogsForLockId)}
          onClose={() => closeLogsDialog(setOpenLogsForLockId)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Access Logs</DialogTitle>
          <DialogContent>
            <AccessLogs lockId={openLogsForLockId} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => closeLogsDialog(setOpenLogsForLockId)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={handleCloseRenameDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rename Lock</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <TextField
              label="Address"
              value={renameLockDetails.address}
              onChange={(e) =>
                setRenameLockDetails((prev) => ({
                  ...prev,
                  address: e.target.value,
                }))
              }
              fullWidth
              required
            />
            <TextField
              label="City"
              value={renameLockDetails.city}
              onChange={(e) =>
                setRenameLockDetails((prev) => ({
                  ...prev,
                  city: e.target.value,
                }))
              }
              fullWidth
              required
            />
            <TextField
              label="State"
              value={renameLockDetails.state}
              onChange={(e) =>
                setRenameLockDetails((prev) => ({
                  ...prev,
                  state: e.target.value,
                }))
              }
              fullWidth
              required
            />
            <TextField
              label="Zip"
              value={renameLockDetails.zip}
              onChange={(e) =>
                setRenameLockDetails((prev) => ({
                  ...prev,
                  zip: e.target.value,
                }))
              }
              fullWidth
              required
            />
            <TextField
              label="Lock Name"
              value={renameLockDetails.lockName}
              onChange={(e) =>
                setRenameLockDetails((prev) => ({
                  ...prev,
                  lockName: e.target.value,
                }))
              }
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRenameDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmRename}
            variant="contained"
            disabled={isRenaming}
          >
            {isRenaming ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Transfer {selectedLockIds.size} Lock
          {selectedLockIds.size !== 1 ? "s" : ""}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Recipient Email"
            type="email"
            value={transferEmail}
            onChange={(e) => setTransferEmail(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmTransfer}
            variant="contained"
            disabled={!transferEmail || transferLoading}
          >
            {transferLoading ? <CircularProgress size={20} /> : "Transfer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Generate Vendor Link Dialog */}
      <GenerateVendorLinkDialog
        open={vendorLinkDialogOpen}
        onClose={() => setVendorLinkDialogOpen(false)}
        listingId={listingId}
        propertyAddress={propertyAddress}
      />

      {/* Add Tenant Dialog (Placeholder) */}

      <AddTenantDialog
        open={addTenantDialogOpen}
        onClose={() => setAddTenantDialogOpen(false)}
        listingId={listingId}
        propertyAddress={propertyAddress}
        availableLocks={matchingLocks}
        onTenantAdded={() => {
          setTenantRefreshTrigger((prev) => prev + 1);
          setAddTenantDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default PropertyDetailView;

